import { isEmpty } from "lodash";
import { parseApiDate } from "../../../../../helpers/dates";
import { createUTCDate } from "../../../../../helpers/tools";


export const generateStartAndEndDate = (date) => {
  const parsedDate = createUTCDate(date);

  const startDate = parsedDate.startOf("month").format("YYYY-MM-DD");
  const endDate = parsedDate.endOf("month").format("YYYY-MM-DD");

  return { startDate, endDate };
};

/**
 * @param {Number} hour
 * @param {Number} minute
 * @returns {string} HH:mm
 */
export const formatHourToString = (hour, minute) => {
  const hourMoment = createUTCDate({ hour, minute });
  return hourMoment.format("HH:mm");
};

/**
 * @param {Object} time
 * @param {string} time.hour
 * @param {string} time.minute
 * @param {number} valueAdded
 * @param {string} unit
 * @param {Moment} currentDate
 * @returns {Object}
 */
export const parseDatesAndTime = (time, valueAdded, unit, currentDate) => {
  const startDate = createUTCDate(currentDate).set({
    hour: time.hour,
    minute: time.minute,
  });
  const endDate = startDate.clone().add(valueAdded, unit);
  return { startDate, endDate };
};

/**
 * @param {Number} value
 * @param {Number} total
 * @returns {Number}
 */
export const calculatePercentage = (value, total) => {
  // Una regla con `capacity.max: 0` daba `NaN`, y `NaN >= 100` es false: el día
  // se pintaba verde y el turno quedaba habilitado con "0 Cupos". Sin capacidad
  // no hay porcentaje que calcular, así que se devuelve `null` y el turno se
  // excluye del promedio en vez de contar como vacío.
  if (
    typeof value !== "number" ||
    typeof total !== "number" ||
    value < 0 ||
    total <= 0
  ) {
    return null;
  }

  return Math.min(100, (value / total) * 100);
};

export const sortOptions = (options) => {
  return options?.sort((a, b) => {
    if (a.label < b.label) return -1;
    if (a.label > b.label) return 1;
    return 0;
  });
};

/**
 * @param {string} stringDate YYYYMMDD
 * @returns {string} YYYY-MM-DD
 */
export const formatAvailabilityDates = (stringDate) => {
  const [year, month, day] = [
    stringDate.slice(0, 4),
    stringDate.slice(4, 6),
    stringDate.slice(6, 8),
  ];
  return `${year}-${month}-${day}`;
};

/**
 * Ocupación de un turno según lo que informa el back.
 *
 * Un turno bloqueado (salida privada) o sin cupo cuenta como lleno: el
 * porcentaje `occupied / maxCapacity` no puede representarlo porque el bloqueo
 * es independiente de cuántos lugares se ocuparon.
 *
 * @param {Object} entry
 * @param {Boolean} entry.blocked
 * @param {Number} entry.available
 * @param {Number} entry.occupiedPercentage
 * @returns {Number|null} null cuando el turno no tiene un porcentaje calculable
 * (regla con máximo 0), para poder excluirlo del promedio.
 */
export const getEntryOccupancyPercentage = (entry) => {
  if (entry?.blocked) return 100;

  // Una regla con máximo 0 llega con `available: 0`, igual que un turno lleno.
  // Se chequea antes de leerlo para no contarla como 100% ocupada: no hay nada
  // que ocupar, así que el turno se excluye del promedio del día.
  if (!Number.isFinite(entry?.occupiedPercentage)) return null;

  if (entry.available <= 0) return 100;

  return entry.occupiedPercentage;
};

/**
 * Promedio de ocupación del día, sólo para distinguir verde de amarillo entre
 * los días que sí venden. Que el día sea seleccionable o no lo decide
 * `isSellable`, no este número.
 *
 * @param {Array} parsedDates
 * @param {Moment} parsedDates[].date
 * @param {Array} parsedDates[].entries
 * @param {string} parsedDates[].entries[].startTimeId
 * @param {Boolean} parsedDates[].entries[].blocked
 * @param {Number} parsedDates[].entries[].occupied
 * @param {Number} parsedDates[].entries[].available
 * @param {Number} parsedDates[].entries[].occupiedPercentage
 * @returns {Array}
 */

export const calculateAverageOccupancyPercentage = (parsedDates = []) => {
  return parsedDates?.map((dateEntry) => {
    const { entries, date } = dateEntry;

    const percentages = (entries ?? [])
      .map(getEntryOccupancyPercentage)
      .filter((percentage) => typeof percentage === "number");

    // Las `entries` se propagan porque es lo único que llega al componente y
    // `getDates` / `addAvailabilityColor` necesitan decidir por turno.
    if (isEmpty(percentages)) {
      return { date, entries, occupiedPercentage: null };
    }

    const totalOccupiedPercentage = percentages.reduce(
      (sum, percentage) => sum + percentage,
      0
    );

    return {
      date,
      entries,
      occupiedPercentage: totalOccupiedPercentage / percentages.length,
    };
  });
};

/**
 *
 * @param {Object} availableDates
 * @returns {Array}
 */
export const groupDates = (availableDates) => {
  const dateGroups = {};
  Object.entries(availableDates).forEach(([startTimeId, objectDate]) => {
    Object.entries(objectDate).forEach(([dateStr, entry]) => {
      const dateString = parseApiDate(formatAvailabilityDates(dateStr));

      if (!dateGroups[dateString]) {
        dateGroups[dateString] = {
          date: dateString,
          entries: [],
        };
      }

      dateGroups[dateString].entries.push({
        startTimeId: startTimeId,
        available: entry.available,
        blocked: !!entry.blocked,
        // Crudo a propósito: una salida privada no pregunta "¿queda cupo?" sino
        // "¿está vacío?", y eso no se puede leer de `available`.
        occupied: entry.occupied,
        occupiedPercentage: calculatePercentage(
          entry.occupied,
          entry.maxCapacity
        ),
      });
    });
  });

  const result = Object.values(dateGroups);
  return result;
};
